import { useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight, Clock, Globe } from 'lucide-react';
import type { PropertyTourBookableWindow, PropertyTourSlot, ProspectTourHome } from '../../services/tenantApi';
import {
  easternDateKey,
  easternZoneAbbrev,
  formatEasternClock,
  formatEasternLongDate,
  formatEasternMonthYear,
} from '../../utils/eastern-week';
import SchedulingConflictDialog, {
  type SchedulingConflictReason,
  type SchedulingConflictSubmit,
} from './SchedulingConflictDialog';
import UnitPhotoCarousel from './UnitPhotoCarousel';
import { formatTourBedBath, formatTourHomeLabel } from '../../utils/tour-home';

const DAY_HEADERS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'] as const;
const DAY_CELL = 'flex aspect-square w-full items-center justify-center rounded-md text-sm';

interface TourUnitChoice {
  building: boolean;
  unitId: string;
  propertyId: string | null;
  unitNumber: string;
  propertyName: string;
  bedrooms: number;
  bathrooms: number;
  rank: number;
  imageUrl: string | null;
  photos: string[];
}

interface CalendarSlot {
  windowId: string;
  slot: PropertyTourSlot;
  blocked: boolean;
}

interface TourDateTimePickerProps {
  homes: ProspectTourHome[];
  windows: PropertyTourBookableWindow[];
  discoveryCallShowed: boolean;
  busy: boolean;
  notice: string | null;
  /** Success (green) vs alert (amber/rose) styling for inline status messages. */
  noticeKind?: 'success' | 'alert';
  onBook: (windowId: string, startAt: string) => void;
  onCancel: (bookingId: string) => void;
  onSubmitConflict: (input: SchedulingConflictSubmit) => Promise<void>;
  children?: ReactNode;
}

const NO_TOUR_MESSAGE = 'Please check back later. No tours are currently available for this property.';
const DISCOVERY_INCOMPLETE_SLOT_LABEL =
  'Available - Discovery call must be completed prior to booking.';

function slotIsBookable(state: PropertyTourSlot['state']): boolean {
  return state === 'open' || state === 'discovery_required';
}

function conflictDefaultReason(slots: CalendarSlot[]): SchedulingConflictReason {
  const hasOpen = slots.some((item) => item.slot.state === 'open');
  return hasOpen ? 'CANNOT_MAKE_OFFERED_TIME' : 'NO_SLOTS_LEFT';
}

function tourUnits(homes: ProspectTourHome[], windows: PropertyTourBookableWindow[]): TourUnitChoice[] {
  const byId = new Map<string, TourUnitChoice>();
  for (const home of homes) {
    const photos = home.photos.length > 0 ? home.photos : home.imageUrl ? [home.imageUrl] : [];
    byId.set(home.unitId, {
      building: home.building,
      unitId: home.unitId,
      propertyId: home.propertyId,
      unitNumber: home.unitNumber,
      propertyName: home.propertyName,
      bedrooms: home.bedrooms,
      bathrooms: home.bathrooms,
      rank: home.rank,
      imageUrl: home.imageUrl ?? photos[0] ?? null,
      photos,
    });
  }
  for (const window of windows) {
    if (byId.has(window.unitId)) continue;
    const photos = window.photos.length > 0 ? window.photos : window.imageUrl ? [window.imageUrl] : [];
    byId.set(window.unitId, {
      building: window.building,
      unitId: window.unitId,
      propertyId: window.propertyId,
      unitNumber: window.unitNumber,
      propertyName: window.propertyName,
      bedrooms: window.bedrooms,
      bathrooms: window.bathrooms,
      rank: window.rank,
      imageUrl: window.imageUrl ?? photos[0] ?? null,
      photos,
    });
  }
  return [...byId.values()].sort(
    (a, b) => a.rank - b.rank || a.propertyName.localeCompare(b.propertyName) || a.unitNumber.localeCompare(b.unitNumber),
  );
}

function slotsForUnit(windows: PropertyTourBookableWindow[], unitId: string): CalendarSlot[] {
  return windows
    .filter((window) => window.unitId === unitId)
    .flatMap((window) => {
      const groupHasTour = window.slots.some((slot) => slot.state === 'yours');
      return window.slots.map((slot) => ({
        windowId: window.id,
        slot,
        blocked: groupHasTour && slotIsBookable(slot.state),
      }));
    });
}

function dateKeyForSlot(slot: PropertyTourSlot): string {
  return easternDateKey(new Date(slot.startAt));
}

function preferredDate(slots: CalendarSlot[]): string | null {
  const keys = [...new Set(slots.map((item) => dateKeyForSlot(item.slot)))].sort();
  const bookable = keys.find((key) =>
    slots.some(
      (item) =>
        dateKeyForSlot(item.slot) === key &&
        slotIsBookable(item.slot.state) && !item.blocked,
    ),
  );
  if (bookable) return bookable;
  const yours = keys.find((key) => slots.some((item) => dateKeyForSlot(item.slot) === key && item.slot.state === 'yours'));
  return yours ?? keys[0] ?? null;
}

function slotKey(windowId: string, startAt: string): string {
  return `${windowId}|${startAt}`;
}

const PICK_LABELS = ['Top pick', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth'];

function shortlistPickLabel(rank: number): string {
  return PICK_LABELS[rank - 1] ?? `Pick ${rank}`;
}

function UnitCover({
  imageUrl,
  className,
}: {
  imageUrl: string | null;
  className: string;
}): ReactElement {
  return (
    <div className={`overflow-hidden bg-gray-100 ${className}`}>
      {imageUrl ? (
        <img src={imageUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full items-center justify-center px-2 text-center text-xs text-gray-400 dark:text-gray-500">No photo</div>
      )}
    </div>
  );
}

function shiftMonth(year: number, month: number, delta: number): { year: number; month: number } {
  const next = new Date(year, month + delta, 1);
  return { year: next.getFullYear(), month: next.getMonth() };
}

function noticeBannerClasses(kind: 'success' | 'alert'): string {
  if (kind === 'success') {
    return 'mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-200';
  }
  return 'mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-300';
}

export default function TourDateTimePicker({
  homes,
  windows,
  discoveryCallShowed,
  busy,
  notice,
  noticeKind = 'alert',
  onBook,
  onCancel,
  onSubmitConflict,
  children,
}: TourDateTimePickerProps): ReactElement {
  const units = useMemo(() => tourUnits(homes, windows), [homes, windows]);
  const [selectedUnitId, setSelectedUnitId] = useState(units[0]?.unitId ?? '');
  const [selectedDate, setSelectedDate] = useState<string | null>(() =>
    units[0] ? preferredDate(slotsForUnit(windows, units[0].unitId)) : null,
  );
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [conflictOpen, setConflictOpen] = useState(false);
  const [conflictError, setConflictError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, []);
  const timesRef = useRef<HTMLDivElement>(null);
  const initialMonth = selectedDate ?? easternDateKey(new Date());
  const [viewYear, setViewYear] = useState(Number(initialMonth.slice(0, 4)));
  const [viewMonth, setViewMonth] = useState(Number(initialMonth.slice(5, 7)) - 1);

  const selectedUnit = units.find((unit) => unit.unitId === selectedUnitId) ?? units[0] ?? null;
  const unitSlots = useMemo(
    () => (selectedUnit ? slotsForUnit(windows, selectedUnit.unitId) : []),
    [selectedUnit, windows],
  );
  const awaitingTour = selectedUnit != null && unitSlots.length === 0;
  const canReportConflict = selectedUnit != null && !awaitingTour;

  useEffect(() => {
    if (units.length === 0) return;
    if (units.some((unit) => unit.unitId === selectedUnitId)) return;
    const next = units[0];
    setSelectedUnitId(next.unitId);
    setSelectedDate(preferredDate(slotsForUnit(windows, next.unitId)));
    setPendingKey(null);
  }, [selectedUnitId, units, windows]);

  useEffect(() => {
    if (canReportConflict) return;
    setConflictOpen(false);
    setConflictError(null);
  }, [canReportConflict]);

  useEffect(() => {
    if (!selectedUnit) return;
    const dates = new Set(unitSlots.map((item) => dateKeyForSlot(item.slot)));
    if (selectedDate && dates.has(selectedDate)) return;
    const nextDate = preferredDate(unitSlots);
    if (nextDate !== selectedDate) setSelectedDate(nextDate);
  }, [selectedDate, selectedUnit, unitSlots]);

  useEffect(() => {
    if (!selectedDate) return;
    setViewYear(Number(selectedDate.slice(0, 4)));
    setViewMonth(Number(selectedDate.slice(5, 7)) - 1);
  }, [selectedDate]);

  useEffect(() => {
    if (!pendingKey) return;
    const match = unitSlots.find((item) => slotKey(item.windowId, item.slot.startAt) === pendingKey);
    if (!match || match.blocked || !slotIsBookable(match.slot.state)) setPendingKey(null);
  }, [pendingKey, unitSlots]);

  const tourDateKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const item of unitSlots) keys.add(dateKeyForSlot(item.slot));
    return keys;
  }, [unitSlots]);

  const yoursDateKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const item of unitSlots) {
      if (item.slot.state === 'yours') keys.add(dateKeyForSlot(item.slot));
    }
    return keys;
  }, [unitSlots]);

  const daySlots = useMemo(() => {
    if (!selectedDate) return [];
    return unitSlots
      .filter((item) => {
        if (slotIsBookable(item.slot.state) && new Date(item.slot.startAt).getTime() <= now) {
          return false;
        }
        return dateKeyForSlot(item.slot) === selectedDate && !item.blocked;
      })
      .sort((a, b) => a.slot.startAt.localeCompare(b.slot.startAt));
  }, [now, selectedDate, unitSlots]);

  const blockedElsewhere = useMemo(() => {
    if (!selectedDate || daySlots.length > 0) return null;
    const windowIds = new Set(
      unitSlots.filter((item) => dateKeyForSlot(item.slot) === selectedDate).map((item) => item.windowId),
    );
    if (windowIds.size === 0) return null;
    return (
      unitSlots.find((item) => windowIds.has(item.windowId) && item.slot.state === 'yours' && item.slot.bookingId) ??
      null
    );
  }, [daySlots.length, selectedDate, unitSlots]);

  const todayKey = easternDateKey(new Date());
  const zone = easternZoneAbbrev();
  const openSlotCount = daySlots.filter((item) => slotIsBookable(item.slot.state)).length;
  const takenSlotCount = daySlots.filter((item) => item.slot.state === 'taken').length;
  const busySlotCount = daySlots.filter((item) => item.slot.state === 'busy').length;
  const availabilityLabel =
    daySlots.length === 0
      ? null
      : openSlotCount === 0
        ? 'No tour slots available'
        : takenSlotCount > 0
          ? `${openSlotCount} of ${daySlots.length} tour slots available`
          : openSlotCount === 1
            ? '1 tour slot available'
            : `${openSlotCount} tour slots available`;

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstWeekday = new Date(viewYear, viewMonth, 1).getDay();
  const cells: Array<number | null> = [];
  for (let index = 0; index < firstWeekday; index += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) cells.push(day);

  const monthHasTour = [...tourDateKeys].some((key) => {
    return Number(key.slice(0, 4)) === viewYear && Number(key.slice(5, 7)) - 1 === viewMonth;
  });

  const selectUnit = (unitId: string): void => {
    if (unitId === selectedUnitId) return;
    const nextSlots = slotsForUnit(windows, unitId);
    const nextDates = new Set(nextSlots.map((item) => dateKeyForSlot(item.slot)));
    setSelectedUnitId(unitId);
    setPendingKey(null);
    setSelectedDate((current) => (current && nextDates.has(current) ? current : preferredDate(nextSlots)));
  };

  const selectDate = (dateKey: string): void => {
    setSelectedDate(dateKey);
    setPendingKey(null);
    if (window.matchMedia('(max-width: 1023px)').matches) {
      window.setTimeout(() => {
        timesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    }
  };

  const goMonth = (delta: number): void => {
    const next = shiftMonth(viewYear, viewMonth, delta);
    setViewYear(next.year);
    setViewMonth(next.month);
  };

  return (
    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2 lg:items-stretch">
      <section className="flex h-full min-h-0 flex-col rounded-2xl border border-gray-200 bg-white shadow-bento dark:border-white/10 dark:bg-gray-800">
        {selectedUnit ? (
          <>
            <div className="aspect-video w-full shrink-0 overflow-hidden">
              <UnitPhotoCarousel
                key={selectedUnit.unitId}
                photos={
                  selectedUnit.photos.length > 0
                    ? selectedUnit.photos
                    : selectedUnit.imageUrl
                      ? [selectedUnit.imageUrl]
                      : []
                }
                label={formatTourHomeLabel(selectedUnit.unitNumber, selectedUnit.propertyName, selectedUnit.building)}
                fill
              />
            </div>
            <div className="flex min-w-0 items-center gap-x-3 px-4 py-3 sm:px-5">
              <p className="shrink-0 text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">Tour</p>
              <h2 className="min-w-0 truncate text-sm font-semibold text-gray-900 dark:text-white">
                {formatTourHomeLabel(selectedUnit.unitNumber, selectedUnit.propertyName, selectedUnit.building)}
              </h2>
              <p className="shrink-0 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                {selectedUnit.building
                  ? 'Building tour · 1–4 bed'
                  : formatTourBedBath(selectedUnit.bedrooms, selectedUnit.bathrooms)}
              </p>
              <p className="ml-auto flex shrink-0 items-center gap-1.5 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                <Clock className="h-4 w-4 shrink-0" aria-hidden="true" />
                15 min
              </p>
            </div>
          </>
        ) : (
          <div className="aspect-video w-full shrink-0 bg-gray-100 dark:bg-gray-900" />
        )}
        {units.length > 0 ? (
          <div className="flex min-h-[5.5rem] shrink-0 flex-col border-t border-gray-100 px-4 py-4 dark:border-white/5 sm:px-5">
            <p className="shrink-0 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Choose a home</p>
            {/* Card is h-12 cover + p-2 + border (4.125rem); space-y-2 is the gap. Show 4 full cards and half of the next. */}
            <div className="mt-2 max-h-[calc(4.5*4.125rem+2rem)] min-h-0 space-y-2 overflow-y-auto pr-1">
              {units.map((unit) => {
                const selected = unit.unitId === selectedUnit?.unitId;
                const label = formatTourHomeLabel(unit.unitNumber, unit.propertyName, unit.building);
                const pick = shortlistPickLabel(unit.rank);
                return (
                  <button
                    key={unit.unitId}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => selectUnit(unit.unitId)}
                    className={`flex w-full items-center gap-3 rounded-xl border p-2 text-left transition-colors ${
                      selected
                        ? 'border-red-600 bg-red-50 dark:border-red-500 dark:bg-red-950/70'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 dark:border-white/10 dark:hover:border-white/20 dark:hover:bg-white/5'
                    }`}
                  >
                    <UnitCover imageUrl={unit.imageUrl} className="h-12 w-16 shrink-0 rounded-lg" />
                    <span className="min-w-0 flex-1">
                      <span className={`block truncate text-sm font-semibold ${selected ? 'text-red-900 dark:text-red-100' : 'text-gray-900 dark:text-gray-100'}`}>
                        {label}
                      </span>
                      <span className={`mt-0.5 block truncate text-xs ${selected ? 'text-red-700 dark:text-red-300' : 'text-gray-500 dark:text-gray-400'}`}>
                        {pick}
                      </span>
                    </span>
                    <span className={`shrink-0 text-right text-xs font-medium ${selected ? 'text-red-800 dark:text-red-200' : 'text-gray-600 dark:text-gray-300'}`}>
                      {unit.building
                        ? 'Building tour'
                        : formatTourBedBath(unit.bedrooms, unit.bathrooms)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="flex min-h-[5.5rem] shrink-0 flex-col border-t border-gray-100 px-4 py-4 dark:border-white/5 sm:px-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Choose a home</p>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">No homes on the shortlist yet.</p>
          </div>
        )}
      </section>

      <div className="flex min-w-0 flex-col gap-4">
      <section className="relative flex shrink-0 flex-col rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 p-4 shadow-bento sm:p-5 min-h-[min(22rem,52dvh)]">
        <div className={awaitingTour ? 'pointer-events-none select-none opacity-40 grayscale' : undefined} aria-hidden={awaitingTour || undefined}>
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">Select a Date & Time</h2>
        <div className="mt-4 flex flex-col gap-5 lg:flex-row lg:items-start">
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-medium text-gray-800 dark:text-gray-100">{formatEasternMonthYear(viewYear, viewMonth)}</p>
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => goMonth(-1)}
                className="rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-gray-100"
                aria-label="Previous month"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => goMonth(1)}
                className="rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-gray-100"
                aria-label="Next month"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-1">
            {DAY_HEADERS.map((label) => (
              <div key={label} className="pb-1 text-center text-[11px] font-medium text-gray-400 dark:text-gray-500">
                {label}
              </div>
            ))}
            {cells.map((day, index) => {
              if (day === null) return <div key={`empty-${index}`} className={DAY_CELL} />;
              const dateKey = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const hasTour = tourDateKeys.has(dateKey);
              const isSelected = dateKey === selectedDate;
              const isToday = dateKey === todayKey;
              const hasYours = yoursDateKeys.has(dateKey);
              if (!hasTour) {
                return (
                  <span
                    key={dateKey}
                    className={`${DAY_CELL} ${
                      isToday ? 'font-semibold text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'
                    }`}
                  >
                    {day}
                  </span>
                );
              }
              return (
                <button
                  key={dateKey}
                  type="button"
                  aria-pressed={isSelected}
                  aria-label={formatEasternLongDate(dateKey)}
                  onClick={() => selectDate(dateKey)}
                  className={`${DAY_CELL} relative bg-red-600 font-semibold text-white hover:bg-red-700 ${
                    isSelected ? 'ring-2 ring-inset ring-white' : ''
                  }`}
                >
                  {day}
                  {hasYours ? (
                    <span className="absolute bottom-1 h-1 w-1 rounded-full bg-white" aria-hidden="true" />
                  ) : null}
                </button>
              );
            })}
          </div>

          {!monthHasTour ? (
            <p className="mt-3 text-center text-xs text-gray-500 dark:text-gray-400">No tour days this month.</p>
          ) : null}

          <p className="mt-4 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <Globe className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" aria-hidden="true" />
            Eastern Time ({zone})
          </p>
        </div>
        <div ref={timesRef} className="min-w-0 flex-1 border-t border-gray-100 pt-5 dark:border-white/5 lg:border-l lg:border-t-0 lg:pl-5 lg:pt-0">
        <h2 className="text-base font-medium text-gray-900 dark:text-white" aria-live="polite">
          {selectedDate ? formatEasternLongDate(selectedDate) : 'Select a date'}
        </h2>
        {notice ? (
          <p role="status" className={noticeBannerClasses(noticeKind)}>
            {notice}
          </p>
        ) : null}
        {availabilityLabel ? (
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{availabilityLabel}</p>
        ) : null}
        {busySlotCount > 0 ? (
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Other home means your group is already touring somewhere else at that time.
          </p>
        ) : null}

        {daySlots.length > 0 ? (
          <div className="mt-4 flex items-center justify-between text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <span>Tour time</span>
            <span>Tour slots</span>
          </div>
        ) : null}

        <div className="mt-2 space-y-2 pr-1">
          {daySlots.map((item) => {
            const key = slotKey(item.windowId, item.slot.startAt);
            const timeLabel = formatEasternClock(item.slot.startAt);
            if (item.slot.state === 'yours' && item.slot.bookingId) {
              return (
                <div
                  key={key}
                  className="flex items-center justify-between gap-2 rounded-lg bg-red-600 px-3 py-3 text-sm font-semibold text-white"
                >
                  <span>{timeLabel}</span>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onCancel(item.slot.bookingId as string)}
                    className="rounded bg-white dark:bg-gray-800 px-2 py-1 text-xs font-semibold text-red-700 dark:text-red-400 disabled:opacity-60"
                  >
                    Cancel
                  </button>
                </div>
              );
            }
            if (item.slot.state === 'taken') {
              return (
                <div
                  key={key}
                  className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 px-3 py-3 text-sm"
                >
                  <span className="font-medium text-gray-400 dark:text-gray-500 line-through">{timeLabel}</span>
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Taken</span>
                </div>
              );
            }
            if (item.slot.state === 'busy') {
              return (
                <div
                  key={key}
                  className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 px-3 py-3 text-sm"
                >
                  <span className="font-medium text-gray-500 dark:text-gray-400">{timeLabel}</span>
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Other home</span>
                </div>
              );
            }
            if (!slotIsBookable(item.slot.state)) {
              return null;
            }
            if (!discoveryCallShowed) {
              return (
                <div
                  key={key}
                  className="flex items-center justify-between gap-3 rounded-lg border border-gray-300 bg-gray-50 px-3 py-3 text-sm dark:border-white/15 dark:bg-white/5"
                >
                  <span className="shrink-0 font-medium text-gray-600 dark:text-gray-400">{timeLabel}</span>
                  <span className="min-w-0 text-right text-xs font-medium leading-snug text-gray-500 dark:text-gray-400">
                    {DISCOVERY_INCOMPLETE_SLOT_LABEL}
                  </span>
                </div>
              );
            }
            const pending = pendingKey === key;
            if (pending) {
              return (
                <div key={key} className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setPendingKey(null)}
                    className="w-[42%] rounded-lg border border-red-600 py-3 text-sm font-semibold text-red-700 dark:text-red-400"
                  >
                    {timeLabel}
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onBook(item.windowId, item.slot.startAt)}
                    className="flex-1 rounded-lg bg-red-600 py-3 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                  >
                    {busy ? 'Booking…' : 'Confirm'}
                  </button>
                </div>
              );
            }
            return (
              <button
                key={key}
                type="button"
                disabled={busy}
                onClick={() => setPendingKey(key)}
                className="flex w-full items-center justify-between gap-2 rounded-lg border border-red-600 px-3 py-3 text-sm font-semibold text-red-700 dark:text-red-400 transition-colors hover:bg-red-50 dark:hover:bg-red-950/50 disabled:opacity-60"
              >
                <span>{timeLabel}</span>
                <span className="text-xs font-semibold uppercase tracking-wide">Available</span>
              </button>
            );
          })}

          {daySlots.length === 0 && blockedElsewhere?.slot.bookingId ? (
            <div className="rounded-lg bg-red-50 dark:bg-red-950/40 px-3 py-3 text-sm text-red-900 dark:text-red-200">
              <p className="font-semibold">Your group already booked this tour.</p>
              <p className="mt-1">
                {formatEasternLongDate(dateKeyForSlot(blockedElsewhere.slot))} at{' '}
                {formatEasternClock(blockedElsewhere.slot.startAt, true)}
              </p>
              <button
                type="button"
                disabled={busy}
                onClick={() => onCancel(blockedElsewhere.slot.bookingId as string)}
                className="mt-3 text-sm font-semibold text-rose-700 dark:text-rose-300 disabled:opacity-60"
              >
                Cancel tour
              </button>
            </div>
          ) : null}

          {selectedDate && daySlots.length === 0 && !blockedElsewhere ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No open times on this day.</p>
          ) : null}
        </div>
        </div>
        </div>
        </div>
        {awaitingTour ? (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-8">
            <p className="max-w-xs text-center text-sm font-semibold text-gray-800 dark:text-gray-100">
              {NO_TOUR_MESSAGE}
            </p>
          </div>
        ) : null}
        {canReportConflict ? (
          <div className="mt-auto flex justify-end pt-4">
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setConflictError(null);
                setConflictOpen(true);
              }}
              className="rounded-lg border border-red-600 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60 dark:text-red-300 dark:hover:bg-red-950/40"
            >
              {conflictDefaultReason(unitSlots) === 'NO_SLOTS_LEFT' ? 'No slots left?' : "Can't make these times?"}
            </button>
          </div>
        ) : null}
      </section>
      {conflictOpen && selectedUnit && canReportConflict ? (
        <SchedulingConflictDialog
          homeLabel={formatTourHomeLabel(selectedUnit.unitNumber, selectedUnit.propertyName, selectedUnit.building)}
          building={selectedUnit.building}
          unitId={selectedUnit.unitId}
          propertyId={selectedUnit.propertyId}
          reason={conflictDefaultReason(unitSlots)}
          busy={busy}
          error={conflictError}
          onClose={() => {
            if (busy) return;
            setConflictOpen(false);
            setConflictError(null);
          }}
          onSubmit={(input) => {
            setConflictError(null);
            void onSubmitConflict(input)
              .then(() => {
                setConflictOpen(false);
              })
              .catch((err: unknown) => {
                setConflictError(err instanceof Error ? err.message : 'Unable to save those times.');
              });
          }}
        />
      ) : null}
      {children}
      </div>
    </div>
  );
}
